import type {
    ADKContentPart,
    ADKEvent,
    ChatMessage,
    EmbeddedDashboard,
    ThoughtPart,
} from "~/pages/chat-agent/types/chatTypes";
import {
    DEFAULT_REQUEST_INPUT_QUESTION,
    extractRequestInputAnswer,
    HUMAN_INPUT_TOOL_NAMES,
} from "~/pages/chat-agent/utils/requestInput";
import {parseInitiativeProposal} from "~/sit/chat/initiativeProposal";

export const generateMessageId = () => Math.random().toString(36).substring(2, 9);

type WireRecord = Record<string, unknown>;

const asRecord = (value: unknown): WireRecord | undefined =>
    value != null && typeof value === 'object' ? value as WireRecord : undefined;

/** A key in its camelCase spelling, else its snake_case one. */
const pick = (o: WireRecord | undefined, camel: string, snake: string) => o?.[camel] ?? o?.[snake];

/**
 * ADK serializes events in camelCase, but its Python field names (snake_case) are accepted too. Every
 * key the chat reads is mapped to camelCase here, once, as an event arrives — nothing else checks both.
 */
export function normalizeADKEvent(raw: unknown): ADKEvent {
    const event = asRecord(raw) ?? {};
    const content = asRecord(event.content);
    const actions = asRecord(event.actions);
    const parts = Array.isArray(content?.parts) ? content.parts.map(asRecord) : undefined;
    return {
        ...event,
        invocationId: pick(event, 'invocationId', 'invocation_id'),
        longRunningToolIds: pick(event, 'longRunningToolIds', 'long_running_tool_ids'),
        errorCode: pick(event, 'errorCode', 'error_code'),
        errorMessage: pick(event, 'errorMessage', 'error_message'),
        actions: actions && {
            ...actions,
            stateDelta: pick(actions, 'stateDelta', 'state_delta'),
            artifactDelta: pick(actions, 'artifactDelta', 'artifact_delta'),
            skipSummarization: pick(actions, 'skipSummarization', 'skip_summarization'),
        },
        content: content && {
            ...content,
            parts: parts?.map(part => ({
                ...part,
                functionCall: pick(part, 'functionCall', 'function_call'),
                functionResponse: pick(part, 'functionResponse', 'function_response'),
                codeExecutionResult: pick(part, 'codeExecutionResult', 'code_execution_result'),
            })),
        },
    } as ADKEvent;
}

/**
 * ADK's `Event.is_final_response()`: the event ends an agent's turn — a long-running call pauses it,
 * a tool result shown without summarizing ends it, and otherwise a complete event with no function
 * calls or responses (and no trailing code-execution result) does.
 */
export function isFinalResponse(event: ADKEvent): boolean {
    if (event.actions?.skipSummarization || event.longRunningToolIds?.length) return true;
    const parts = event.content?.parts ?? [];
    return event.partial !== true
        && !parts.some(p => p.functionCall || p.functionResponse)
        && parts[parts.length - 1]?.codeExecutionResult == null;
}

/**
 * Parses one `data:` payload of /run_sse, or returns null when it is not JSON. ADK's API server
 * reports a run that raised as `{"error": "..."}` instead of an event, which is thrown here.
 */
export function parseSseData(data: string): ADKEvent | null {
    let raw: unknown;
    try {
        raw = JSON.parse(data);
    } catch {
        console.warn('Failed to parse SSE event:', data);
        return null;
    }
    const error = asRecord(raw)?.error;
    if (error != null) {
        throw new Error(typeof error === 'string' ? error : JSON.stringify(error));
    }
    return normalizeADKEvent(raw);
}

type MessageMeta = Pick<ChatMessage, 'id' | 'author' | 'invocationId' | 'timestamp'>;

/**
 * Who sent a message and when, plus its id. The id is derived from the ADK event, so a streamed message
 * and its reloaded copy match. One event can become several messages, so every message but its text
 * bubble adds a `suffix` naming its part (a call id, "thought", "stopped", "error"): ids that collide
 * break the chat list's React keys and let applyLiveText update the wrong message.
 */
function messageMeta(event: ADKEvent, suffix?: string): MessageMeta {
    const id = event.id || generateMessageId();
    return {
        id: suffix ? `${id}-${suffix}` : id,
        author: event.author,
        invocationId: event.invocationId,
        timestamp: event.timestamp ? new Date(event.timestamp * 1000) : new Date(),
    };
}

/** Parses the `embedded` object from a dashgen tool response, if present and well-formed. */
function parseEmbeddedDashboard(response: unknown): EmbeddedDashboard | undefined {
    if (response == null || typeof response !== 'object') return undefined;
    const embedded = (response as {embedded?: unknown}).embedded;
    if (embedded == null || typeof embedded !== 'object') return undefined;
    const o = embedded as Record<string, unknown>;
    if (typeof o.uuid !== 'string' || typeof o.superset_domain !== 'string') return undefined;
    return {
        uuid: o.uuid,
        superset_domain: o.superset_domain,
        dashboard_id: Number(o.dashboard_id) || 0,
        title: typeof o.title === 'string' ? o.title : '',
    };
}

/** A paused `adk_request_input` becomes a question bubble; any other call a tool-progress bubble. */
function mapFunctionCallPart(
    event: ADKEvent,
    functionCall: NonNullable<ADKContentPart['functionCall']>,
): ChatMessage {
    const callId = functionCall.id;
    const isPausedForInput =
        HUMAN_INPUT_TOOL_NAMES.has(functionCall.name) &&
        !!callId &&
        !!event.longRunningToolIds?.includes(callId);

    if (isPausedForInput) {
        return {
            ...messageMeta(event, callId),
            type: 'bot',
            content: typeof functionCall.args?.message === 'string'
                ? functionCall.args.message
                : DEFAULT_REQUEST_INPUT_QUESTION,
            event_type: 'request_input',
            requestInput: {callId: callId!, toolName: functionCall.name},
        };
    }
    return {
        ...messageMeta(event, callId),
        type: 'bot',
        content: functionCall.name,
        event_type: 'function_call',
        callId,
    };
}

function mapFunctionResponsePart(
    event: ADKEvent,
    functionResponse: NonNullable<ADKContentPart['functionResponse']>,
): ChatMessage {
    // The user's answer to a paused adk_request_input (see buildRequestInputResponse) is persisted
    // as a user-authored functionResponse, not text — reconstruct it as the answer bubble it was.
    // findPendingInput relies on every answer having one.
    if (event.author === 'user' && HUMAN_INPUT_TOOL_NAMES.has(functionResponse.name)) {
        const content = extractRequestInputAnswer(functionResponse.response);
        return {...messageMeta(event), type: 'user', content, event_type: 'text'};
    }
    return {
        ...messageMeta(event, functionResponse.id),
        type: 'bot',
        content: functionResponse.name,
        event_type: 'function_response',
        embedded: parseEmbeddedDashboard(functionResponse.response),
        initiativeProposal: parseInitiativeProposal(functionResponse.response),
        callId: functionResponse.id,
    };
}

/**
 * The parts of a live SSE event worth showing, per ADK's streaming guidance: a partial chunk only
 * previews text, and its function calls are half-built, so only its text is kept. The final event of
 * the response is the complete one ADK persists — every part is kept: its calls carry the real id and
 * `longRunningToolIds`, and its text settles the streamed bubble (see applyLiveText). Stored events
 * are never partial, so reload maps every part and ends up with the same messages. With
 * `thoughtsStreamed`, the final event's thoughts already arrived as chunks and are dropped.
 */
export function toLiveEvent(event: ADKEvent, thoughtsStreamed = false): ADKEvent {
    if (!event.content?.parts) return event;
    if (event.partial === true) {
        const parts = event.content.parts.filter(p => Boolean(p.text));
        return {...event, content: {...event.content, parts}};
    }
    if (!thoughtsStreamed) return event;
    const parts = event.content.parts.filter(p => !p.thought);
    return {...event, content: {...event.content, parts}};
}

/**
 * Applies the text bubble of a live event. ADK gives every partial chunk of one model response the
 * same event id as its final event, and the final event repeats the whole text: a chunk extends the
 * still-partial bubble with that id, the final event replaces its text with the complete one (so missed
 * chunks don't stick), and a chunk arriving after its response is final is dropped.
 */
export function applyLiveText(messages: ChatMessage[], text: ChatMessage, partial: boolean): ChatMessage[] {
    const idx = messages.findIndex(m => m.id === text.id);
    if (idx < 0) {
        return [...messages, {...text, isPartial: partial}];
    }
    const current = messages[idx];
    if (partial && !current.isPartial) {
        return messages;
    }
    const next = [...messages];
    next[idx] = {
        ...current,
        content: partial ? current.content + text.content : text.content,
        timestamp: text.timestamp,
        invocationId: current.invocationId ?? text.invocationId,
        isPartial: partial,
        endsTurn: text.endsTurn,
    };
    return next;
}

/** `errorCode` ADK saves when a run is cancelled, e.g. after Stop closed its connection. */
export const INVOCATION_ABORTED = 'INVOCATION_ABORTED';

/** Closes a turn the user stopped (see ChatMessage.event_type "stopped"); `meta` dates it when saved. */
export function stoppedMarker(meta?: MessageMeta): ChatMessage {
    return {
        id: generateMessageId(),
        timestamp: new Date(),
        ...meta,
        type: 'bot',
        content: '',
        event_type: 'stopped',
    };
}

/**
 * Ends a turn that was cut off (stopped, or the request failed) with `ending`. Text still streaming
 * keeps what arrived, since no final event will settle it.
 */
export function endInterruptedTurn(messages: ChatMessage[], ending: ChatMessage): ChatMessage[] {
    const settled = messages.map(m => m.isPartial ? {...m, isPartial: false} : m);
    return [...settled, ending];
}

export function mapADKEventToChatMessages(event: ADKEvent): ChatMessage[] {
    const messages: ChatMessage[] = [];
    // One text bubble per event: it is keyed by the event id, which streamed chunks share with their final event.
    let text: ChatMessage | null = null;

    for (const part of event.content?.parts ?? []) {
        if (part.thought) {
            if (part.text && event.author !== 'user') {
                messages.push({...messageMeta(event, 'thought'), type: 'bot', content: part.text, event_type: 'thought'});
            }
        } else if (part.text) {
            if (text) {
                text.content += part.text;
                continue;
            }
            text = {
                ...messageMeta(event),
                type: event.author === 'user' ? 'user' : 'bot',
                content: part.text,
                event_type: 'text',
            };
            messages.push(text);
        } else if (part.functionCall) {
            messages.push(mapFunctionCallPart(event, part.functionCall));
        } else if (part.functionResponse) {
            messages.push(mapFunctionResponsePart(event, part.functionResponse));
        }
    }

    // The turn may end on these replies. Thoughts alone never answer, so they never end it.
    if (isFinalResponse(event)) {
        for (const message of messages) {
            if (message.type === 'bot' && message.event_type !== 'thought') message.endsTurn = true;
        }
    }

    // ADK persists errors, so live streaming and reload end the turn alike: a cancelled run as stopped,
    // a model or tool failure as failed.
    if (event.errorCode === INVOCATION_ABORTED) {
        messages.push(stoppedMarker(messageMeta(event, 'stopped')));
    } else if ((event.errorCode || event.errorMessage) && event.author !== 'user') {
        messages.push({
            ...messageMeta(event, 'error'),
            type: 'bot',
            content: `Error: ${event.errorMessage || event.errorCode}`,
            event_type: 'text',
            isError: true,
        });
    }

    return messages;
}

/** Finds the function_call a function_response resolves — matched by call id when available. */
function findMatchingFunctionCallIndex(messages: ChatMessage[], responseCallId: string | undefined): number {
    for (let i = messages.length - 1; i >= 0; i--) {
        const candidate = messages[i];
        if (candidate.event_type !== 'function_call') continue;
        if (responseCallId == null || candidate.callId === responseCallId) return i;
    }
    return -1;
}

/**
 * Marks the call a function_response resolves as answered (in place, keeping the call's id so its
 * row stays put), or appends the response when no pending call matches.
 */
export function applyFunctionResponse(messages: ChatMessage[], response: ChatMessage): void {
    const idx = findMatchingFunctionCallIndex(messages, response.callId);
    if (idx < 0) {
        messages.push(response);
        return;
    }
    const call = messages[idx];
    messages[idx] = {
        ...call,
        event_type: 'function_response',
        invocationId: call.invocationId ?? response.invocationId,
        toolProgress: undefined,
        embedded: response.embedded,
        initiativeProposal: response.initiativeProposal,
        respondedAt: response.timestamp,
        endsTurn: response.endsTurn,
    };
}

/** Index of the thinking section of the current turn (messages after the last user message), or -1. */
export function findTurnThoughtIndex(messages: ChatMessage[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].type === 'user') return -1;
        if (messages[i].event_type === 'thought') return i;
    }
    return -1;
}

/**
 * Appends a thought into the turn's single thinking section (in place), or starts that section.
 * Streamed chunks continue the current paragraph; a thought resumed after a tool call or reply
 * text starts a new one.
 */
export function appendThought(messages: ChatMessage[], thought: ChatMessage): void {
    const idx = findTurnThoughtIndex(messages);
    if (idx < 0) {
        messages.push({...thought, thoughtTailId: thought.id, thoughtParts: [{id: thought.id, text: thought.content}]});
        return;
    }
    const section = messages[idx];
    const tailId = messages[messages.length - 1].id;
    const separator = tailId === section.thoughtTailId ? '' : '\n\n';
    messages[idx] = {
        ...section,
        content: section.content + separator + thought.content,
        invocationId: section.invocationId ?? thought.invocationId,
        thoughtTailId: tailId,
        thoughtParts: addThoughtPart(section.thoughtParts ?? [], {id: thought.id, text: separator + thought.content}),
    };
}

/** Streamed chunks of one event extend its part; another event's thought adds one. */
function addThoughtPart(parts: ThoughtPart[], part: ThoughtPart): ThoughtPart[] {
    const last = parts[parts.length - 1];
    if (last?.id === part.id) {
        return [...parts.slice(0, -1), {id: last.id, text: last.text + part.text}];
    }
    return [...parts, part];
}

/**
 * Adds to a reloaded thinking section `section` the thoughts of the section that streamed meanwhile
 * (`streamed`) from events it lacks — they arrived after the reload was read, or were still streaming
 * then, since ADK persists a response only once it is complete. Thoughts of the events it has are
 * dropped, as it holds them already.
 */
export function mergeThoughtSections(section: ChatMessage, streamed: ChatMessage): ChatMessage {
    const known = new Set((section.thoughtParts ?? []).map(p => p.id));
    // The first new thought follows the reloaded ones, so it starts a paragraph of its own.
    const added = (streamed.thoughtParts ?? [])
        .filter(p => !known.has(p.id))
        .map((p, i) => i === 0 ? {...p, text: `\n\n${p.text.replace(/^\n\n/, '')}`} : p);
    if (added.length === 0) return section;
    return {
        ...section,
        content: section.content + added.map(p => p.text).join(''),
        thoughtTailId: streamed.thoughtTailId,
        thoughtParts: [...(section.thoughtParts ?? []), ...added],
    };
}

/**
 * For a final-response event with nothing to show (e.g. only a state change): the turn ends on the
 * reply before it (in place).
 */
export function markTurnEnded(messages: ChatMessage[]): void {
    const last = messages[messages.length - 1];
    if (last?.type === 'bot' && last.event_type !== 'thought' && !last.endsTurn) {
        messages[messages.length - 1] = {...last, endsTurn: true};
    }
}

/** Rebuilds the chat transcript from stored session events, folding each function_response into its call. */
export function buildMessagesFromADKEvents(events: ADKEvent[]): ChatMessage[] {
    const messages: ChatMessage[] = [];

    for (const event of events) {
        const mapped = mapADKEventToChatMessages(event);
        if (mapped.length === 0 && event.author !== 'user' && isFinalResponse(event)) {
            markTurnEnded(messages);
        }
        for (const msg of mapped) {
            if (msg.event_type === 'thought') {
                appendThought(messages, msg);
            } else if (msg.event_type === 'function_response') {
                applyFunctionResponse(messages, msg);
            } else {
                messages.push(msg);
            }
        }
    }

    return messages;
}
